import { Reminder } from "@models/reminder";
import { CronUtil } from "@utils/cron.util";
import { Companion } from '@models/companion';

const MS_IN_MONTH = 30 * 24 * 60 * 60 * 1000;
const KEEP_ALIVE_INTERVAL = 25 * 1000;

export enum NotificationEvents {
  NOTIFICATION = "NOTIFICATION",
  SCHEDULE_NOTIFICATION = "SCHEDULE_NOTIFICATION",
  REMOVE_NOTIFICATIONS = "REMOVE_NOTIFICATIONS",
  KEEP_ALIVE = "KEEP_ALIVE",
  POLL_NOTIFICATIONS = "POLL_NOTIFICATIONS",
}

class KeepWorkerAlive {
  constructor(private readonly serviceWorker: ServiceWorker) {}

  private interval: ReturnType<typeof setInterval> | null = null;

  start(): void {
    this.interval = setInterval(() => {
      this.serviceWorker.postMessage({
        type: NotificationEvents.KEEP_ALIVE,
      });
    }, KEEP_ALIVE_INTERVAL);
  }

  stop(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }
}

export class NotificationService {
  public static instance = new NotificationService();

  private keepAlive: KeepWorkerAlive | null = null;

  public subscribeToMessages(onNotification: (e: MessageEvent) => void): void {
    if (!("serviceWorker" in navigator)) {
      return;
    }
    navigator.serviceWorker.addEventListener("message", onNotification);
  }

  public unsubscribeFromMessages(
    onNotification: (e: MessageEvent) => void,
  ): void {
    if (!("serviceWorker" in navigator)) {
      return;
    }
    navigator.serviceWorker.removeEventListener("message", onNotification);
  }

  public async unsubscribeAll(): Promise<void> {
    if (this.keepAlive) {
      this.keepAlive.stop();
      this.keepAlive = null;
    }

    const worker = await this.getServiceWorker();
    if (!worker) {
      return;
    }

    worker.postMessage({
      type: NotificationEvents.REMOVE_NOTIFICATIONS,
    });
  }

  async scheduleNotifications(reminders: Reminder[], _defaultCompanion?: Companion): Promise<void> {
    const worker = await this.getServiceWorker();
    if (!worker) {
      return;
    }

    if (this.keepAlive) {
      this.keepAlive.stop();
    }
    this.keepAlive = new KeepWorkerAlive(worker);
    this.keepAlive.start();

    worker.postMessage({
      type: NotificationEvents.REMOVE_NOTIFICATIONS,
    });

    if (!reminders.length) {
      return;
    }
    const messages: any[] = [];

    reminders.forEach((reminder) => {
      const timestamps = CronUtil.getNextTimestamps(
        reminder.schedule,
        Date.now() + MS_IN_MONTH,
      );
      if (!timestamps.length) {
        return;
      }
      messages.push({
        type: NotificationEvents.SCHEDULE_NOTIFICATION,
        title: reminder.title,
        options: {
          icon: `${process.env.PUBLIC_URL}/images/logo.png`,
          badge: `${process.env.PUBLIC_URL}/images/logo.png`,
          body: reminder.description,
        } as NotificationOptions,
        timestamps,
        id: reminder.id,
      });
    });

    if (!messages.length) {
      return;
    }

    if (Notification.permission === "default") {
      await Notification.requestPermission();
    }
    if (Notification.permission !== "granted") {
      return;
    }

    messages.forEach((message) => {
      worker.postMessage(message);
    });
  }

  private async getServiceWorker(): Promise<ServiceWorker | null> {
    if (!("serviceWorker" in navigator)) {
      return null;
    }
    await navigator.serviceWorker.ready;
    const registration = await navigator.serviceWorker.getRegistration();
    return registration?.active || null;
  }
}
